import React, { Component, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import update from 'immutability-helper';
import * as _ from 'lodash';
import moment from 'moment';

import { toastr } from 'react-redux-toastr';
import { isPetasenseAdmin, isOrgAdmin } from 'common/rbac/util';
import RBAC from 'common/rbac/RBAC';
import { ResourceInputField, ResourceItemLabel, ResourceItem } from 'common/components/atoms/ResourceDetails';
import InlineInfoNote from 'common/components/atoms/InlineInfoNote';
import { Grid, Segment, Dropdown } from 'semantic-ui-react';
import Label_T from 'common/typography/Label/Label';
import H2_T from 'common/typography/H2/H2';
import DissociateConfirmPrompt from 'common/components/organisms/AlertPrompt';
import colors from 'common/styles/colors';
import { getSensorTypeDisplayName } from 'common/utils/sensorTypeDisplayName';
import * as sensorsActions from '../../actions/sensors.actions';
import LoadingSvg from '../../../../common/images/LoadingSvg';
import FlexContainer from '../../../../common/components/atoms/FlexContainer';
import CrossSvg from '../../../../common/images/CrossSvg';
import Button_T from '../../../../common/components/atoms/Button';
import ExpandableContainer from '../../../../common/components/molecules/ExpandableContainer';
import { ExclamationTriangle, Info, Wrench } from '../../../../common/images/FaIcons';
import MeasurementScheduleModal from '../MeasurementScheduleModal';
import * as sensorUtils from '../../utils/sensorUtils';
import {
  hasTransmitterMachineAssociation,
  resolveOrganisationId,
  resolveDeviceTransferParams,
  hasTransmitterAttachedSensors,
  collectReassignBlockers,
  hasReassignBlockers,
  buildReassignDisabledHint,
} from '../../utils/siteReassignUtils';
import { translateSensorSelectOptions } from '../../utils/sensorMeasurementDisplay';
import {
  AGGRESSIVE_INTERVAL_THRESHOLD,
  StandardSensors,
  VectorSensors,
  ErrorMessages,
  vibrationMeasurementTypeEnum,
  vibrationMeasurementBlocks,
  oldTxMeasurementVersion
} from '../../constants/sensors.constants';
import WifiSetting from '../WifiSettings';
import TxPorts from '../TxPorts';
import PreferredAPSettings from '../PreferredAPSettings';
import { parseErrorMessage } from '../../../../common/helpers/errors';
import { getDebugModeSerials, excludeDebugModeSerials } from '../../utils/debugModeUtils';
import ReassignToSiteModal from '../ReassignToSiteModal';

const Container = styled(FlexContainer).attrs({ direction: 'column' })`
  padding: 2em;
  min-height: Calc(100vh - var(--title-bar-offset, 0px) - 138px);
  position: relative;
`;

const SvgContainer = styled.span`
  cursor: pointer;
  margin-left: auto;
`;

const ResourceHeaderSection = styled(H2_T)`
  width: 100%;
  font-size: 18px;
  padding-bottom: 18px;
`;

const Button = styled(Button_T)`
  width: max-content;
  ${props => props.fontsize && `font-size: ${props.fontsize};`}
`;

const SetButton = styled(Button_T)`
  width: max-content;
  padding-left: 165px;
  ${props => props.fontsize && `font-size: ${props.fontsize};`}
`;

const H2 = styled(H2_T)`
  padding: 0px;
`;

const LoadingContainer = styled.div`
  align-self: center;
  margin: auto;
`;

const ButtonsContainer = styled(FlexContainer)`
  padding-top: 2em;
  align-items: flex-start;
  flex-wrap: wrap;
  button:first-child {
    margin-right: 15px;
  }
`;

// Semantic's `icon` button collapses the gap between label and caret, which
// reads as one crowded word on longer labels like "Unassign sensors".
const DissociateDropdownContainer = styled.span`
  .ui.dropdown.button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding-right: 14px;
  }

  .ui.dropdown.button > .dropdown.icon {
    margin: 0;
    opacity: 0.65;
  }
`;

// InlineInfoNote is built for a toolbar row (margin-left: auto). Inside the
// button row that would fling it to the far edge, away from the button.
const ReassignNote = styled(InlineInfoNote)`
  margin-left: 10px;
  align-self: center;
`;

const AbsoluteLoading = styled.div`
  background: rgba(233, 233, 233, 0.7);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: all;
  overflow: hidden;
`;

const TextLabel = styled(Label_T)`
  font-weight: 600;
  margin-bottom: 0;
  margin-left: 8px;
  margin-top: -2px;
`;

const DebugHintLabel = styled(TextLabel)`
  display: block;
  margin-left: 0;
  margin-bottom: 8px;
`;

const Labelcontainer = styled(FlexContainer)`
  padding-top: 2em;
  height: max-content;
  &:after {
    content:"";
    display: inline-block;
    height: 0.5em;
    align-self: center;
    width: Calc(100% - 270px);
    margin-right: -880px;
    margin-left: 10px;
    border-top: 1px solid ${props => props.theme.colors.greyD};
    margin-top: 0.5em;
  }
`;

const HeaderContainer = styled(FlexContainer).attrs({
  justifyContent: 'space-between',
  alignItems: 'center',
})`
  padding-bottom: 1em;
`;

const calculateOtherSensorGroupedName = (arr, t, i18n) => arr.reduce((acc, cur, idx) => {
  if (acc.includes(cur.type)) return acc;
  const typeLabel = getSensorTypeDisplayName(t, i18n, cur.type);
  if (idx === 0) return `${typeLabel} - ${cur.model}`;
  if (idx === 2) return `${acc} ...`;
  if (idx > 2) return acc;
  return `${acc} & ${typeLabel} - ${cur.model}`;
}, '');

// The measurement blocks a mote reports depend on its model, so the grouped
// editor can never assume all three are there. Exported for the unit tests,
// which exercise the helpers without mounting the (very large) render tree.
export const allMeasurementBlockFields = vibrationMeasurementBlocks.map(({ field }) => field);

export class GroupedSensors extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sensorList: props.sensorList || [],
      details: {},
      fieldValues: {},
      initialFieldValues: {},
      error: false,
      editMode: props.editMode,
      saveEnabled: false,
      expandedContainers: {},
      actualModel: '',
      showReassignSiteModal: false,
      reassigning: false,
      dissociatePrompt: null
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const sensorSerialNoList = this.props.sensorList.map(s => s.serial_number);
    const filteredDetails = {};
    Object.keys(this.props.details).forEach((model) => {
      filteredDetails[model] = this.props.details[model].filter(s => sensorSerialNoList.includes(s.serial_number));
      if (filteredDetails[model].length === 0) delete filteredDetails[model];
    });
    let newDetails = this.splitDetailsAsPerTXConfig(filteredDetails);
    newDetails = this.splitDetailsAsPerTemperatureOnly(newDetails);
    // Check if props.details changed (e.g., after save) or if state.details differs from props
    const propsDetailsChanged = !_.isEqual(prevProps.details, this.props.details);
    const stateDetailsDiffer = !_.isEqual(this.state.details, newDetails);
    if (propsDetailsChanged || stateDetailsDiffer) {
      this.setState({
        details: newDetails
      }, this.calculateGroupedModelFieldValues);
    }
    if (!_.isEqual(this.state.sensorList, this.props.sensorList)) {
      this.setState({
        sensorList: this.props.sensorList
      });
    }
    if (prevState.editMode !== this.props.editMode) {
      this.setState({
        editMode: this.props.editMode
      });
    }
    // Compare fieldValues with initialFieldValues
    const hasChanges = this.hasFieldValuesChanged(this.state.fieldValues, this.state.initialFieldValues);
    const shouldEnableSave = hasChanges;
    if (shouldEnableSave && this.state.saveEnabled !== true) {
      this.setState({ saveEnabled: true });
    } else if (!shouldEnableSave && this.state.saveEnabled !== false) {
      this.setState({ saveEnabled: false });
    }
  }

  hasFieldValuesChanged = (currentFieldValues, initialFieldValues) => {
    // Compare each model's field values
    const currentModels = Object.keys(currentFieldValues);
    const initialModels = Object.keys(initialFieldValues);

    // If different number of models, there are changes
    if (currentModels.length !== initialModels.length) {
      return true;
    }

    // Compare each model using array methods
    return currentModels.some((model) => {
      const current = currentFieldValues[model];
      const initial = initialFieldValues[model];

      if (!initial) {
        return true; // New model added
      }

      // Get all keys from both objects
      const allKeys = [...new Set([...Object.keys(current), ...Object.keys(initial)])];

      // Check if any field has changed - use deep equality for all fields
      return allKeys.some(key => !_.isEqual(current[key], initial[key]));
    });
  }

  splitDetailsAsPerTXConfig = (details) => {
    const newDetails = {};
    Object.keys(details).forEach((model) => {
      newDetails[model] = details[model].filter(s => s.transmitter_version !== '0804');
      newDetails[`${model} - 0804`] = details[model].filter(s => s.transmitter_version === '0804');
      if (newDetails[model].length === 0) delete newDetails[model];
      if (newDetails[`${model} - 0804`].length === 0) delete newDetails[`${model} - 0804`];
    });
    return newDetails;
  };

  splitDetailsAsPerTemperatureOnly = (details) => {
    const newDetails = {};
    Object.keys(details).forEach((model) => {
      newDetails[model] = details[model].filter(s => s.temperature_only !== true);
      newDetails[`${model} - Temperature Only`] = details[model].filter(s => s.temperature_only === true);
      if (newDetails[model].length === 0) delete newDetails[model];
      if (newDetails[`${model} - Temperature Only`].length === 0) delete newDetails[`${model} - Temperature Only`];
    });
    return newDetails;
  };

  unassignSensorFromTx = (sensorSerialNumber, txSerialNumber) => {
    this.props.sensorsActions.unassignSensorFromTx({
      serial_numbers: [sensorSerialNumber],
      tx_serial_no: txSerialNumber
    }).then(
      () => {
        this.state.details.TX.forEach((tx) => {
          if (tx.serial_number === txSerialNumber) return;
          let txUpdated = false;
          tx.ports.forEach((port) => {
            if (_.some(port.sensors, sensor => sensor.serial_number === sensorSerialNumber)) txUpdated = true;
          });
          if (txUpdated) this.props.sensorsActions.getSensorDetails(tx.serial_number);
        });
        this.setState(prevState => ({ expandedContainers: { ...prevState.expandedContainers, TX: true } }));
      }
    );
  }

  calculateGroupedModelFieldValues = () => {
    const { details } = this.state;
    const fieldValues = Object.entries(details).reduce((acc, cur) => {
      if ([...StandardSensors, ...VectorSensors].includes(cur[1][0].type)) {
        acc[cur[0]] = this.calculateSameFieldForType(cur[1], cur[1][0].model, cur[1][0].type);
      } else acc[cur[0]] = this.calculateSameFieldForType(cur[1], 'other', 'other');
      return acc;
    }, {});
    this.setState({ fieldValues, initialFieldValues: fieldValues });
  };

  calculateSameFieldForType = (arr, model, type) => {
    const { metadata } = this.props;
    if (!metadata[model]) {
      this.setState({
        error: `No metadata for given model! ${model}`
      });
      return null;
    }
    if (type === 'Vibration Mote') {
      // A mote only carries the measurement blocks its model reports — a
      // temperature-only or short-payload device has no full_bandwidth /
      // high_res at all (see getSensorDetails), and reading `.type` off the
      // missing block used to take the whole grouped view down. Every block is
      // materialised below (an absent one as an empty object) so the readers
      // here and in render() resolve to the usual blank value instead of
      // throwing; `available_blocks` records which ones are real so
      // makeResponseForType never echoes a block the device does not have.
      // A block counts as available only when every sensor in the group reports
      // it — the group is edited as a unit, so a partial block is not editable.
      const available_blocks = allMeasurementBlockFields.filter(
        field => arr.every(data => Boolean(data[field]))
      );
      const blockOf = (data, field) => data[field] || {};
      // The measurement type is shared across blocks, so read it off whichever
      // block this model actually reports rather than assuming `overall`.
      const typeOf = (data) => {
        const block = available_blocks.map(field => data[field]).find(Boolean);
        return block ? block.type : '';
      };
      let overall = {};
      let full_bandwidth = {};
      let high_res = {};
      let measurement_settings_type = '';
      let reporting_interval = '';
      const serial_number = [];
      arr.forEach((data, idx) => {
        const dataOverall = blockOf(data, 'overall');
        const dataFullBandwidth = blockOf(data, 'full_bandwidth');
        const dataHighRes = blockOf(data, 'high_res');
        if (idx === 0) {
          overall = _.cloneDeep(dataOverall);
          full_bandwidth = _.cloneDeep(dataFullBandwidth);
          high_res = _.cloneDeep(dataHighRes);
          measurement_settings_type = typeOf(data);
          reporting_interval = _.cloneDeep(data.reporting_interval);
        } else {
          if (overall.sampling && !_.isEqual(overall.sampling, dataOverall.sampling)) {
            overall.sampling = '';
          }
          if (full_bandwidth.sampling && !_.isEqual(full_bandwidth.sampling, dataFullBandwidth.sampling)) {
            full_bandwidth.sampling = '';
          }
          if (high_res.sampling && !_.isEqual(high_res.sampling, dataHighRes.sampling)) {
            high_res.sampling = '';
          }
          if (reporting_interval && !_.isEqual(reporting_interval, data.reporting_interval)) {
            reporting_interval = '';
          }
          if (!_.isEqual(overall.schedule, dataOverall.schedule)) {
            overall.schedule = [];
            overall.stops = [];
          }
          if (!_.isEqual(full_bandwidth.schedule, dataFullBandwidth.schedule)) {
            full_bandwidth.schedule = [];
            full_bandwidth.stops = [];
          }
          if (!_.isEqual(high_res.schedule, dataHighRes.schedule)) {
            high_res.schedule = [];
            high_res.stops = [];
          }
          if (measurement_settings_type && !_.isEqual(measurement_settings_type, typeOf(data))) {
            measurement_settings_type = '';
            overall.schedule = [];
            overall.stops = [];
            full_bandwidth.schedule = [];
            full_bandwidth.stops = [];
            high_res.schedule = [];
            high_res.stops = [];
          }
        }
        serial_number.push(data.serial_number);
      });
      overall.type = measurement_settings_type;
      full_bandwidth.type = measurement_settings_type;
      high_res.type = measurement_settings_type;
      return {
        overall,
        full_bandwidth,
        high_res,
        available_blocks,
        reporting_interval,
        serial_number,
        type,
        model,
        scheduled_saved: false,
        wifi_schedule_time: '00:00',
        wifi_schedule_date: moment().add(1, 'days').format('YYYY-MM-DD')
      };
    }
    if (VectorSensors.includes(type)) {
      let measurement_type = '';
      let measurement_interval = '';
      let full_bandwidth = {};
      let high_res = {};
      const serial_number = [];
      arr.forEach((data, idx) => {
        if (idx === 0) {
          measurement_type = data.measurement_type;
          measurement_interval = data.measurement_interval;
          full_bandwidth = data.full_bandwidth;
          high_res = data.high_res;
        } else {
          if (!_.isEqual(full_bandwidth.sampling.resolution, data.full_bandwidth.sampling.resolution)) {
            full_bandwidth.sampling.resolution = {};
          }
          if (!_.isEqual(high_res.sampling.resolution, data.high_res.sampling.resolution)) {
            high_res.sampling.resolution = {};
          }
          if (!_.isEqual(measurement_type, data.measurement_type)) {
            measurement_type = '';
          }
          if (!_.isEqual(full_bandwidth.high_pass_filter_cutoff, data.full_bandwidth.high_pass_filter_cutoff)) {
            full_bandwidth.high_pass_filter_cutoff = '';
          }
          if (!_.isEqual(high_res.high_pass_filter_cutoff, data.high_res.high_pass_filter_cutoff)) {
            high_res.high_pass_filter_cutoff = '';
          }
          if (!_.isEqual(full_bandwidth.measurement.schedule, data.full_bandwidth.measurement.schedule)) {
            full_bandwidth.measurement.schedule = [];
          }
          if (!_.isEqual(high_res.measurement.schedule, data.high_res.measurement.schedule)) {
            high_res.measurement.schedule = [];
          }
          if (!_.isEqual(full_bandwidth.measurement.stops, data.full_bandwidth.measurement.stops)) {
            full_bandwidth.measurement.stops = [];
          }
          if (!_.isEqual(high_res.measurement.stops, data.high_res.measurement.stops)) {
            high_res.measurement.stops = [];
          }
          if (!_.isEqual(measurement_interval, data.measurement_interval)) {
            measurement_interval = '';
          }
        }
        serial_number.push(data.serial_number);
      });
      return {
        measurement_type,
        measurement_interval,
        full_bandwidth,
        high_res,
        serial_number,
        model,
        transmitter_version: arr[0].transmitter_version,
        scheduled_saved: false,
        type
      };
    }
    if (type === 'Transmitter') {
      let reporting_interval = '';
      const serial_number = [];
      arr.forEach((data, idx) => {
        if (idx === 0) {
          reporting_interval = data.reporting_interval;
        } else if (!_.isEqual(reporting_interval, data.reporting_interval)) {
          reporting_interval = '';
        }
        serial_number.push(data.serial_number);
      });
      return {
        reporting_interval,
        serial_number,
        model,
        type,
        wifi_schedule_time: '00:00',
        wifi_schedule_date: moment().add(1, 'days').format('YYYY-MM-DD'),
      };
    }
    if (type === 'other') {
      let overall_measurement_type = '';
      let overall_measurement_interval = '';
      let overall_measurement_schedule = [];
      let overall_measurement_stops = [];
      const serial_number = [];
      arr.forEach((data, idx) => {
        if (idx === 0) {
          overall_measurement_type = data.overall_measurement_type;
          overall_measurement_interval = data.overall_measurement_interval;
          overall_measurement_schedule = data.overall_measurement_schedule;
          overall_measurement_stops = data.overall_measurement_stops;
        } else {
          if (!_.isEqual(overall_measurement_type, data.overall_measurement_type)) {
            overall_measurement_type = '';
          }
          if (!_.isEqual(overall_measurement_schedule, data.overall_measurement_schedule)) {
            overall_measurement_schedule = [];
          }
          if (!_.isEqual(overall_measurement_stops, data.overall_measurement_stops)) {
            overall_measurement_stops = [];
          }
          if (!_.isEqual(overall_measurement_interval, data.overall_measurement_interval)) {
            overall_measurement_interval = '';
          }
        }
        serial_number.push(data.serial_number);
      });
      return {
        overall_measurement_schedule,
        overall_measurement_stops,
        overall_measurement_type,
        overall_measurement_interval,
        serial_number,
        model,
        transmitter_version: arr[0].transmitter_version,
        scheduled_saved: false,
        type
      };
    }
    return null;
  };

  isJson = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  handleInputClick = (e, data, model) => {
    const targetValue = e.target.value || data.value || '';
    const targetName = e.target.name || data.name;

    this.onChangeField(e, { value: targetValue }, targetName, model);
  };

  onChangeField = (e, { value }, name, model) => {
    const { fieldValues } = this.state;
    let newDetails = _.cloneDeep(fieldValues[model]);
    const [newName, option] = name.split('.');

    if (name === 'spectrum_sampling_1' || name === 'spectrum_sampling_0') {
      newDetails[name] = {
        ...newDetails[name],
        resolution: this.isJson(value) ? JSON.parse(value) : value
      };
    } else if (name === 'spectrum_measurement_type' || name === 'measurement_settings_type') {
      newDetails = {
        ...newDetails,
        overall: { ...newDetails.overall, type: value },
        full_bandwidth: { ...newDetails.full_bandwidth, type: value },
        high_res: { ...newDetails.high_res, type: value },
      };
    } else if (option === 'sampling' && !_.startsWith(model, 'VM')) {
      newDetails[newName] = {
        ...newDetails[newName],
        sampling: {
          ...newDetails[newName].sampling,
          resolution: this.isJson(value) ? JSON.parse(value) : value
        }
      };
    } else if (option) {
      newDetails[newName] = {
        ...newDetails[newName],
        [option]: this.isJson(value) ? JSON.parse(value) : value
      };
    } else {
      newDetails[name] = this.isJson(value) ? JSON.parse(value) : value;
    }

    this.setState(prevState => ({
      fieldValues: {
        ...prevState.fieldValues,
        [model]: newDetails
      }
    }));
  };

  makeResponseForType = (data) => {
    let resData = {};
    if (data.type === 'Vibration Mote') {
      // Only the blocks the model actually reports; the empty placeholders that
      // calculateSameFieldForType puts in their place must not be sent back, or
      // we would be asking the device to configure a measurement it has no
      // channel for.
      const availableBlocks = data.available_blocks || allMeasurementBlockFields;
      const measurement_settings = {};
      vibrationMeasurementBlocks.forEach(({ index, field }) => {
        if (availableBlocks.includes(field)) measurement_settings[index] = data[field];
      });
      resData = {
        aggressive_measurement_timeout: data.aggressive_measurement_timeout,
        measurement_settings,
        serial_number: data.serial_number,
        type: data.type,
        ...(data.reporting_interval && { reporting: { interval: data.reporting_interval } })
      };

      if (data.ssid && data.passphrase) {
        resData.wifi_config = {
          ssid: data.ssid,
          passphrase: data.passphrase,
          start_time: sensorUtils.getEpochFromDateTime(data.wifi_schedule_date, data.wifi_schedule_time),
        };
      }

      if (data.bssid && data.channel) {
        resData.wifi_ap_config = {
          bssid: data.bssid,
          channel: data.channel,
        };
      }

      if (!data.scheduled_saved) {
        availableBlocks.forEach((field) => {
          delete data[field].schedule;
          delete data[field].stops;
        });
      }
    }

    if (VectorSensors.includes(data.type)) {
      const highResEnabled = !_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
        (data.type === 'Vibration Sensor' || data.type === 'VSX');
      resData = {
        aggressive_measurement_timeout: data.aggressive_measurement_timeout,
        spectrum_measurements: {
          1: {},
          2: highResEnabled ? {} : null
        },
        serial_number: data.serial_number,
        type: data.type
      };

      if (data.measurement_type) {
        if (data.scheduled_saved) {
          resData.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement = {
            type: data.measurement_type,
            schedule: data.full_bandwidth.measurement.schedule,
            stops: data.full_bandwidth.measurement.stops
          };
        } else {
          resData.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement = {
            type: data.measurement_type
          };
        }
      }

      if (!_.isEmpty(data.full_bandwidth.sampling.resolution)) {
        resData.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].sampling = {
          resolution: data.full_bandwidth.sampling.resolution
        };
      }

      if (data.full_bandwidth.high_pass_filter_cutoff) {
        resData.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].high_pass_filter_cutoff = data.full_bandwidth.high_pass_filter_cutoff;
      }

      if (data.measurement_interval) {
        resData.spectrum_measurements[vibrationMeasurementTypeEnum.fullBandwidth].measurement_interval = data.measurement_interval;
      }

      if (highResEnabled) {
        if (data.measurement_type) {
          if (data.scheduled_saved) {
            resData.spectrum_measurements[vibrationMeasurementTypeEnum.highRes].measurement = {
              type: data.measurement_type,
              schedule: data.high_res.measurement.schedule,
              stops: data.high_res.measurement.stops
            };
          } else {
            resData.spectrum_measurements[vibrationMeasurementTypeEnum.highRes].measurement = {
              type: data.measurement_type
            };
          }
        }

        if (!_.isEmpty(data.high_res.sampling.resolution)) {
          resData.spectrum_measurements[vibrationMeasurementTypeEnum.highRes].sampling = {
            resolution: data.high_res.sampling.resolution
          };
        }

        if (data.high_res.high_pass_filter_cutoff) {
          resData.spectrum_measurements[vibrationMeasurementTypeEnum.highRes].high_pass_filter_cutoff = data.high_res.high_pass_filter_cutoff;
        }

        if (data.measurement_interval) {
          resData.spectrum_measurements[vibrationMeasurementTypeEnum.highRes].measurement_interval = data.measurement_interval;
        }
      }

      if (_.isEmpty(resData.spectrum_measurements)) {
        delete resData.spectrum_measurements;
      }
    }

    if (data.type === 'Transmitter') {
      resData = {
        aggressive_measurement_timeout: data.aggressive_measurement_timeout,
        reporting: {
          interval: '',
        },
        serial_number: data.serial_number,
        type: data.type
      };
      if (data.reporting_interval) {
        resData.reporting.interval = data.reporting_interval;
        resData.aggressive_measurement = (
          data.reporting_interval <= AGGRESSIVE_INTERVAL_THRESHOLD
        );
      } else delete resData.reporting;

      if (data.ssid && data.passphrase) {
        resData.wifi_config = {
          ssid: data.ssid,
          passphrase: data.passphrase,
          start_time: sensorUtils.getEpochFromDateTime(data.wifi_schedule_date, data.wifi_schedule_time),
        };
      }

      if (data.bssid && data.channel) {
        resData.wifi_ap_config = {
          bssid: data.bssid,
          channel: data.channel,
        };
      }
    }
    if (data.type === 'other') {
      resData = {
        overall_measurements: {
          measurement: { type: '' },
          measurement_interval: ''
        },
        serial_number: data.serial_number,
        type: data.type
      };
      if (data.overall_measurement_type && data.scheduled_saved) {
        resData.overall_measurements.measurement.type = data.overall_measurement_type;
        resData.overall_measurements.measurement.schedule = data.overall_measurement_schedule;
        resData.overall_measurements.measurement.stops = data.spectrum_measurement_stops;
      } else delete resData.overall_measurements.measurement;
      if (data.overall_measurement_interval) {
        resData.overall_measurements.measurement_interval = data.overall_measurement_interval;
      } else delete resData.overall_measurements.measurement_interval;
      if (_.isEmpty(resData.overall_measurements)) {
        delete resData.overall_measurements;
      }
    }
    return resData;
  };

  validateInput = (model) => {
    const { fieldValues } = this.state;
    const { ssid, passphrase, bssid, channel } = fieldValues[model];

    const errorMessages = {};

    if (ssid || passphrase) {
      if (!passphrase) errorMessages.passphrase_error = ErrorMessages.wifi_password_required;
      if (!ssid) errorMessages.ssid_error = ErrorMessages.wifi_ssid_required;
    }

    if (!_.isEmpty(bssid)) {
      if (!bssid.match(/^([A-Fa-f0-9]{12})$/)) {
        errorMessages.bssid_error = ErrorMessages.invalid_bssid;
      }
    }

    const channelInt = parseInt(channel, 10);
    if (_.isNumber(channelInt) && (channelInt < 1 || channelInt > 13)) {
      errorMessages.channel_error = ErrorMessages.invalid_channel;
    }

    const newDetails = update(fieldValues, {
      [model]: { errorMessages: { $set: errorMessages } }
    });

    this.setState({ fieldValues: newDetails });
  }

  onSave = () => {
    const { updateSensorDetails } = this.props.sensorsActions;
    const { fieldValues } = this.state;
    const data = {};
    Object.entries(fieldValues).forEach((entry) => {
      const payload = excludeDebugModeSerials(
        this.makeResponseForType(entry[1]),
        getDebugModeSerials(this.props.sensorList)
      );
      if (payload) data[entry[1].model] = payload;
    });
    if (Object.keys(data).length === 0) {
      toastr.error(this.props.t('sensors.grouped.debugModeAllSkipped', {
        defaultValue: 'Debug mode is active on every selected device. No changes can be applied.'
      }));
      // Resolve with a status rather than rejecting: onSave is wired straight to
      // onClick and to setState callbacks that discard the return value, so a
      // rejection here would surface as an unhandled rejection (and a Sentry issue).
      return Promise.resolve({ skipped: true });
    }
    return updateSensorDetails(data).then(
      () => {
        // After save, the Redux store will update, which will trigger componentDidUpdate
        // and recalculate fieldValues and initialFieldValues from the updated props
        // This ensures they match and save gets disabled
      },
      error => toastr.error(parseErrorMessage(error))
    );
  };

  onCancel = () => {
    this.calculateGroupedModelFieldValues();
  };

  isListItemAssociatedToMachine = (item) => {
    if (!item) return false;
    if (item.machine_id != null && item.machine_id !== '') return true;
    if (item.location_id != null && item.location_id !== '') return true;
    if (item.machine != null && item.machine !== '') return true;
    if (item.location != null && item.location !== '') return true;
    return false;
  };

  /** Prefer loaded detail rows (same rules as dissociate); fall back to list item fields. */
  isSelectedDeviceAssociated = (listItem) => {
    const detailRow = this.findDetailRowBySerial(listItem.serial_number);
    if (listItem && listItem.type === 'Transmitter') {
      return hasTransmitterMachineAssociation({ sensor: listItem, details: detailRow });
    }
    if (detailRow) {
      return this.isDetailAssociatedToMachine(detailRow);
    }
    return this.isListItemAssociatedToMachine(listItem);
  };

  getReassignContext = () => {
    const { sensorItems, selectedSensorList, currentAccount } = this.props;
    const selected = (sensorItems || []).filter(
      s => (selectedSensorList || []).includes(s.serial_number)
    );
    const orgId = resolveOrganisationId({
      sensor: selected[0],
      currentAccount
    });
    // Name the offending devices, not just the failing gate; with a mixed
    // selection there is otherwise no way to tell which one holds it shut.
    const blockers = collectReassignBlockers(selected, {
      isAssociated: s => this.isSelectedDeviceAssociated(s),
      getDetailRow: s => this.findDetailRowBySerial(s.serial_number)
    });
    const eligible = selected.length > 0 && !hasReassignBlockers(blockers);
    const currentSiteIds = Array.from(new Set(selected.map(s => s.account_id).filter(id => id != null)));
    return {
      orgId: orgId || null,
      canReassign: !!orgId && eligible,
      blockers,
      selected,
      currentSiteIds
    };
  };

  reassignDisabledHint = ctx => buildReassignDisabledHint(ctx.blockers, this.props.t);

  findDetailRowInDetailsMap = (detailsMap, serial) => {
    if (!detailsMap || !serial) return null;
    const keys = Object.keys(detailsMap);
    for (let i = 0; i < keys.length; i += 1) {
      const rows = detailsMap[keys[i]];
      const found = (rows || []).find(r => r.serial_number === serial);
      if (found) return found;
    }
    return null;
  };

  findDetailRowBySerial = (serial) => {
    const { details } = this.state;
    return this.findDetailRowInDetailsMap(details, serial);
  };

  resolveTransferParamsWithPrefetch = async (dev) => {
    const row = this.findDetailRowBySerial(dev.serial_number);
    const first = resolveDeviceTransferParams(dev, row);
    if (first) return first;
    const freshRow = await this.props.sensorsActions.fetchSensorDetailRowForTransfer(
      dev.serial_number
    );
    return resolveDeviceTransferParams(dev, freshRow);
  };

  isDetailAssociatedToMachine = d =>
    (d.machine != null && d.machine !== '') ||
    (d.location != null && d.location !== '');

  // Offer Dissociate whenever there is wiring to clear, not only when a machine
  // is involved: a Tx holding sensors that are on no machine still blocks site
  // reassign, and this dropdown is the only way to unassign them. A Tx detail
  // row carries no machine fields of its own (TxInfoSchema emits only `ports`),
  // so the port check already covers the machine case.
  isDeviceAssociatedForDissociate = (d) => {
    if (!d) return false;
    if (d.type === 'Transmitter') {
      return hasTransmitterAttachedSensors({ details: d });
    }
    return this.isDetailAssociatedToMachine(d);
  };

  // Dissociating a Tx always deletes every sensor-to-port assignment on it --
  // that is how it comes off a machine at all. Motes are dissociated from the
  // machine directly, so only the Tx copy talks about sensors.
  isTransmitterWithAttachedSensors = d => !!d
    && d.type === 'Transmitter'
    && hasTransmitterAttachedSensors({ details: d });

  openDissociatePrompt = (modelKey, serials) => {
    this.setState({ dissociatePrompt: { modelKey, serials } });
  };

  closeDissociatePrompt = () => {
    this.setState({ dissociatePrompt: null });
  };

  reportDissociateBatchResults = (results, wiringOnly = false) => {
    const { t } = this.props;
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;

    if (succeeded > 0 && failed === 0) {
      toastr.success(wiringOnly
        ? t('sensors.grouped.unassignSuccess', {
          count: succeeded,
          defaultValue: 'Unassigned sensors from {{count}} transmitter(s)'
        })
        : t('sensors.grouped.dissociateSuccess', { count: succeeded }));
      return;
    }
    if (succeeded > 0 && failed > 0) {
      toastr.warning(wiringOnly
        ? t('sensors.grouped.unassignPartialSuccess', {
          succeeded,
          failed,
          defaultValue: 'Unassigned sensors from {{succeeded}} transmitter(s); {{failed}} failed.'
        })
        : t('sensors.grouped.dissociatePartialSuccess', { succeeded, failed }));
      return;
    }
    if (failed > 0) {
      const rejected = results.find(r => r.status === 'rejected');
      const reason = rejected && rejected.reason;
      const message = reason && (reason.message || (typeof reason === 'string' ? reason : null));
      toastr.error(message || (wiringOnly
        ? t('sensors.grouped.unassignFailed', {
          count: failed,
          defaultValue: 'Could not unassign sensors from {{count}} transmitter(s)'
        })
        : t('sensors.grouped.dissociateFailed', { count: failed })));
    }
  };

  dissociateTransmitterBatch = (targets, wiringOnly = false) => {
    const { dissociateTxFromMachine } = this.props.sensorsActions;
    return targets.reduce(
      (chain, row) => chain.then(results =>
        dissociateTxFromMachine(row.id, row.serial_number)
          .then(() => [...results, { status: 'fulfilled' }])
          .catch(reason => [...results, { status: 'rejected', reason }])
      ),
      Promise.resolve([])
    ).then(results => this.reportDissociateBatchResults(results, wiringOnly));
  };

  confirmDissociateFromPrompt = () => {
    const { dissociatePrompt } = this.state;
    if (!dissociatePrompt) return;
    const { modelKey, serials } = dissociatePrompt;
    this.closeDissociatePrompt();
    this.performDissociateSerialsForModelGroup(modelKey, serials);
  };

  performDissociateSerialsForModelGroup = (modelKey, serials) => {
    const { fieldValues, details } = this.state;
    const groupDetails = details[modelKey];
    const fv = fieldValues[modelKey];
    if (!groupDetails || !fv || !serials || serials.length === 0) return;

    const targetSerials = serials.filter((sn) => {
      const row = groupDetails.find(d => d.serial_number === sn);
      return row && this.isDeviceAssociatedForDissociate(row);
    });
    if (targetSerials.length === 0) return;

    const sample = groupDetails.find(d => targetSerials.includes(d.serial_number));
    if (!sample) return;

    if (sample.type === 'Transmitter') {
      const targets = targetSerials
        .map(sn => groupDetails.find(d => d.serial_number === sn))
        .filter(row => row && row.id != null);
      if (targets.length !== targetSerials.length) {
        toastr.error(this.props.t('sensors.reassignSite.noTransmitterId'));
        return;
      }
      this.dissociateTransmitterBatch(
        targets,
        targets.every(this.isTransmitterWithAttachedSensors)
      );
      return;
    }

    const { updateSensorDetails } = this.props.sensorsActions;
    const payload = {
      [fv.model]: {
        serial_number: targetSerials,
        type: sample.type,
        location_id: null,
        machine: null,
        machine_name: null,
        component: null,
        component_name: null
      }
    };

    updateSensorDetails(payload).then(
      () => {
        toastr.success(
          this.props.t('sensors.grouped.dissociateSuccess', { count: targetSerials.length })
        );
      },
      (error) => {
        toastr.error(error && error.message ? error.message : String(error));
      }
    );
  };

  renderDebugModeHint = (devices) => {
    const debugSerials = getDebugModeSerials(this.props.sensorList);
    const affected = (devices || [])
      .map(d => d.serial_number)
      .filter(sn => debugSerials.includes(sn));
    if (affected.length === 0) return null;
    return (
      <DebugHintLabel>
        <Wrench />
        {' '}
        {this.props.t('sensors.grouped.debugModeSkipped', {
          serialNumbers: affected.join(', '),
          defaultValue: 'Debug mode is active on {{serialNumbers}}. Changes will not be applied to these devices.'
        })}
      </DebugHintLabel>
    );
  };

  renderBulkDissociateDropdown = (modelKey) => {
    const { t, updating } = this.props;
    const devices = this.state.details[modelKey];
    if (!devices || devices.length < 1) return null;

    const associated = devices.filter(this.isDeviceAssociatedForDissociate);
    if (associated.length === 0) return null;

    // Label by what the action actually does here: with no machine in play it
    // only empties the Tx ports.
    const txWiring = associated.every(this.isTransmitterWithAttachedSensors);
    const actionLabel = txWiring
      ? t('sensors.grouped.unassignSensors', { defaultValue: 'Unassign sensors' })
      : t('sensors.detail.dissociate');
    const allLabel = txWiring
      ? t('sensors.grouped.unassignAllInPanel', { defaultValue: 'Unassign all in this group' })
      : t('sensors.grouped.dissociateAllInPanel');

    return (
      <DissociateDropdownContainer
        role="presentation"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <Dropdown
          direction="left"
          disabled={updating}
          floating
          button
          className="icon"
          icon="dropdown"
          text={actionLabel}
        >
          <Dropdown.Menu>
            {associated.map(d => (
              <Dropdown.Item
                key={d.serial_number}
                text={d.serial_number}
                onClick={() => this.openDissociatePrompt(modelKey, [d.serial_number])}
              />
            ))}
            {/* Show when this panel lists multiple devices; "all" = every row here that is still on a machine */}
            {devices.length > 1 && associated.length > 0 ? (
              <Fragment>
                <Dropdown.Divider />
                <Dropdown.Item
                  text={allLabel}
                  onClick={() => this.openDissociatePrompt(
                    modelKey,
                    associated.map(dev => dev.serial_number)
                  )}
                />
              </Fragment>
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      </DissociateDropdownContainer>
    );
  };

  handleReassignSelectedToSite = async (targetAccountId) => {
    const ctx = this.getReassignContext();
    if (!ctx.orgId || !targetAccountId || ctx.selected.length === 0) return;

    if (!ctx.canReassign) {
      toastr.warning(this.reassignDisabledHint(ctx));
      this.setState({ showReassignSiteModal: false });
      return;
    }

    const allAlreadyOnTarget = ctx.selected.length > 0
      && ctx.selected.every(dev => String(dev.account_id) === String(targetAccountId));
    if (allAlreadyOnTarget) {
      return;
    }

    const { orgId, selected } = ctx;

    this.setState({ reassigning: true });
    const results = { ok: 0, failed: 0, unresolved: 0 };
    let outcomes = [];

    try {
      const perDevice = await selected.reduce(
        async (accPromise, dev) => {
          const acc = await accPromise;
          const params = await this.resolveTransferParamsWithPrefetch(dev);
          return acc.concat([{ dev, params }]);
        },
        Promise.resolve([])
      );

      const jobMap = new Map();
      perDevice.forEach(({ params }) => {
        if (!params) {
          results.failed += 1;
          results.unresolved += 1;
          return;
        }
        const key = `${params.device_type}:${params.device_id}`;
        if (!jobMap.has(key)) {
          jobMap.set(key, { params, count: 0 });
        }
        jobMap.get(key).count += 1;
      });

      const jobs = Array.from(jobMap.values());
      outcomes = await Promise.all(
        jobs.map(({ params, count }) =>
          this.props.sensorsActions
            .transferDeviceToSite({
              orgId,
              ...params,
              target_account_id: targetAccountId,
              silent: true
            })
            .then(() => ({ ok: count, failed: 0 }))
            .catch(() => ({ ok: 0, failed: count }))
        )
      );
      outcomes.forEach((o) => {
        results.ok += o.ok;
        results.failed += o.failed;
      });
    } finally {
      const devicesAffected = results.ok;
      const devicesFailed = results.failed;
      const unresolvedCount = results.unresolved;
      const transferFailureDevices = devicesFailed - unresolvedCount;
      const transfersCompleted = outcomes.filter(o => o.ok > 0).length;
      const transfersFailed = outcomes.filter(o => o.failed > 0).length;

      this.setState({ reassigning: false, showReassignSiteModal: false });
      if (unresolvedCount > 0) {
        toastr.warning(
          this.props.t('sensors.reassignSite.bulkNoTransferParams', {
            count: unresolvedCount,
            defaultValue: '{{count}} device(s) could not be reassigned: transfer target could not be resolved.',
          })
        );
      }
      if (devicesAffected > 0) {
        if (transfersCompleted !== devicesAffected) {
          toastr.success(
            this.props.t('sensors.reassignSite.bulkSuccessWithTransfers', {
              count: transfersCompleted,
              deviceCount: devicesAffected,
              transferCount: transfersCompleted,
              defaultValue: '{{deviceCount}} devices reassigned ({{transferCount}} transfer completed)',
            })
          );
        } else {
          toastr.success(
            this.props.t('sensors.reassignSite.bulkSuccess', { count: devicesAffected })
          );
        }
      }
      if (transferFailureDevices > 0) {
        if (transfersFailed !== transferFailureDevices) {
          toastr.warning(
            this.props.t('sensors.reassignSite.bulkPartialWithTransfers', {
              count: transfersFailed,
              deviceCount: transferFailureDevices,
              transferCount: transfersFailed,
              defaultValue: '{{deviceCount}} devices could not be reassigned ({{transferCount}} transfer failed)',
            })
          );
        } else {
          toastr.warning(
            this.props.t('sensors.reassignSite.bulkPartial', { count: transferFailureDevices })
          );
        }
      }
      if (this.props.onReassignComplete) {
        this.props.onReassignComplete();
      }
    }
  };

  stringifyOptions = options =>
    options.map(option =>
      ({
        ...option,
        value: _.isObject(option.value) ? JSON.stringify(option.value) : option.value
      }));

  renderField = (type, label, options_T, value_T, disabled, name, model, ri_upper_threshold = null) => {
    const { editMode } = this.state;
    const { updating, t } = this.props;

    if (['VM3', 'VM4P', 'GSP', 'VM4', 'GS'].includes(model) && name === 'reporting_interval' && ri_upper_threshold) {
      options_T = options_T.filter(o => o.value <= ri_upper_threshold);
    }

    const options = translateSensorSelectOptions(t, name, this.stringifyOptions(options_T));
    const value = _.isObject(value_T) || _.isArray(value_T) ? JSON.stringify(value_T) : value_T;
    if (editMode) {
      return (
        <ResourceInputField
          type={type}
          label={label}
          options={options}
          value={value}
          name={name}
          key={`${name}-${model}`}
          disabled={disabled || updating}
          onChange={(e, value) => this.onChangeField(e, value, name, model)}
          selectOnBlur={false}
          placeholder={_.isEmpty(value) ? t('sensors.detail.multipleValues') : ''}
        />
      );
    }
    let textValue = '';
    if (options) {
      const element = options.filter(option => _.isEqual(option.value, value))[0];
      if (element) textValue = element.text;
      else textValue = '--';
    }
    return (
      <Fragment>
        <ResourceItemLabel>{label}</ResourceItemLabel>
        <ResourceItem>{textValue}</ResourceItem>
      </Fragment>
    );
  };

  renderContent = (type, model, actualModel, ri_upper_threshold) => {
    const { metadata, t } = this.props;
    if (![...StandardSensors, ...VectorSensors].includes(type)) type = 'other';
    const { fieldValues } = this.state;
    if (!fieldValues[model]) return null;
    if (type !== 'other' && !metadata[actualModel]) {
      this.setState({
        error: t('sensors.errors.noMetadataForModel')
      });
      return null;
    }
    if (type === 'other' && !metadata.other) {
      this.setState({
        error: t('sensors.errors.noMetadataForModel')
      });
      return null;
    }
    const highResEnabled = !_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version);
    switch (type) {
      case 'Vibration Mote': {
        return (
          <Fragment>
            <Labelcontainer>
              <ResourceItemLabel grey fontsize="14px" marginbottom="0" paddingtop="0" width="max-content">
                {t('sensors.detail.fullSpectrumMeasurementSettings')}
              </ResourceItemLabel>
            </Labelcontainer>
            {!model.includes('Temperature Only') && (
            <>
            <ResourceHeaderSection>{t('sensors.detail.sampling')}</ResourceHeaderSection>
            <Grid columns={3} doubling stackable>
              <Grid.Row>
                <Grid.Column>
                  <Segment>
                   {this.renderField(
                     'select',
                     t('sensors.detail.highResolution'),
                     metadata[actualModel].measurement_settings.sampling[vibrationMeasurementTypeEnum.highRes],
                     fieldValues[model].high_res.sampling,
                     false,
                     'high_res.sampling',
                     model
                   )}
                  </Segment>
                </Grid.Column>
                <Grid.Column>
                  <Segment>
                   {this.renderField(
                     'select',
                     t('sensors.detail.fullBandwidth'),
                     metadata[actualModel].measurement_settings.sampling[vibrationMeasurementTypeEnum.fullBandwidth],
                     fieldValues[model].full_bandwidth.sampling,
                     false,
                     'full_bandwidth.sampling',
                     model
                   )}
                  </Segment>
                </Grid.Column>
                <Grid.Column>
                  <Segment>
                   <ResourceItemLabel>{t('sensors.detail.overalls')}</ResourceItemLabel>
                   <FlexContainer marginbottom="0.5em">
                    <Info aria-hidden="true" size="1x" />
                    <TextLabel>{t('sensors.detail.overallsRmsPeakHint')}</TextLabel>
                   </FlexContainer>
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <ResourceHeaderSection>{t('sensors.detail.highPassFilterCutoff')}</ResourceHeaderSection>
            <Grid columns={3} doubling stackable>
              <Grid.Row>
                <Grid.Column>
                  <Segment>
                   {this.renderField(
                     'select',
                     t('sensors.detail.highResolution'),
                     metadata[actualModel].measurement_settings.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.highRes],
                     fieldValues[model].high_res.high_pass_filter_cutoff,
                     false,
                     'high_res.high_pass_filter_cutoff',
                     model
                   )}
                  </Segment>
                </Grid.Column>
                <Grid.Column>
                  <Segment>
                    { metadata[actualModel].measurement_settings.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.fullBandwidth] &&
                     this.renderField(
                       'select',
                       t('sensors.detail.fullBandwidth'),
                       metadata[actualModel].measurement_settings.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.fullBandwidth],
                       fieldValues[model].full_bandwidth.high_pass_filter_cutoff,
                       false,
                       'full_bandwidth.high_pass_filter_cutoff',
                       model
                     )}
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            </>
            )}
            <ResourceHeaderSection>{t('sensors.detail.measurement')}</ResourceHeaderSection>
            <Fragment>
             <Grid.Column>
              <Segment>
                <div style={{ display: 'flex' }}>
                  <div>
                   <ResourceItemLabel>{t('sensors.detail.type')}</ResourceItemLabel>
                   {fieldValues[model].overall.type === 'schedule_based' && (
                      <SetButton text onClick={() => this.setState({ openScheduler: true, modelOpened: model, actualModel, typeOpened: type })} fontsize="12px">
                         {t('sensors.detail.set')}
                      </SetButton>
                   )}
                   {this.renderField(
                     'select',
                     '',
                     sensorUtils.filterStateBasedOption(metadata[actualModel].measurement_settings.type, true),
                     fieldValues[model].overall.type,
                     false,
                     'spectrum_measurement_type',
                     model)
                    }
                  </div>
                </div>
              </Segment>
             </Grid.Column>
            </Fragment>
            <ResourceHeaderSection>{t('sensors.detail.reporting')}</ResourceHeaderSection>
            {model === 'VM1' || model === 'VM2' ? (
              <Grid columns={3} doubling stackable>
               <Grid.Row>
                <Grid.Column>
                  <Segment>
                    <ResourceItemLabel>{t('sensors.detail.highResolution')}</ResourceItemLabel>
                    <FlexContainer marginbottom="0.5em">
                     <Info aria-hidden="true" size="1x" />
                     <TextLabel>{t('sensors.detail.highResSamplesReportedImmediately')}</TextLabel>
                    </FlexContainer>
                  </Segment>
                </Grid.Column>
               <Grid.Column>
                <Segment>
                 <ResourceItemLabel>{t('sensors.detail.fullBandwidth')}</ResourceItemLabel>
                 <FlexContainer marginbottom="0.5em">
                   <Info aria-hidden="true" size="1x" />
                   <TextLabel>{t('sensors.detail.fullBandwidthSamplesReportedImmediately')}</TextLabel>
                 </FlexContainer>
                </Segment>
               </Grid.Column>
               <Grid.Column>
                <Segment>
                 <ResourceItemLabel>{t('sensors.detail.overalls')}</ResourceItemLabel>
                 <FlexContainer marginbottom="0.5em">
                  <Info aria-hidden="true" size="1x" />
                  <TextLabel>{t('sensors.detail.overallsReportedNextInterval')}</TextLabel>
                 </FlexContainer>
                </Segment>
               </Grid.Column>
               </Grid.Row>
              </Grid>
            ) : (
             <Grid columns={3}>
              <Grid.Row>
                <Grid.Column>
                  <Segment>
                    {this.renderField(
                      'select',
                      t('sensors.detail.interval'),
                      metadata[actualModel].reporting.interval,
                      fieldValues[model].reporting_interval,
                      false,
                      'reporting_interval',
                      model,
                      ri_upper_threshold
                    )}
                  </Segment>
                </Grid.Column>
              </Grid.Row>
             </Grid>
            )}
            {['VM3', 'VM1', 'VM2', 'VM4P', 'VM4'].includes(model) && (
              <RBAC
                yes={(
                  <>
                    <ResourceHeaderSection>{t('sensors.detail.wifiSettings')}</ResourceHeaderSection>
                    <WifiSetting
                      ssid={fieldValues[model].ssid}
                      passphrase={fieldValues[model].passphrase}
                      errorMessages={fieldValues[model].errorMessages || {}}
                      wifi_schedule_date={fieldValues[model].wifi_schedule_date}
                      wifi_schedule_time={fieldValues[model].wifi_schedule_time}
                      preferredTimeOptions={this.preferredTimeOptions}
                      handleInputClick={this.handleInputClick}
                      model={model}
                    />
                    <ResourceHeaderSection>{t('sensors.detail.preferredAPSettingsShort')}</ResourceHeaderSection>
                    <PreferredAPSettings
                      bssid={fieldValues[model].bssid}
                      channel={fieldValues[model].channel}
                      errorMessages={fieldValues[model].errorMessages || {}}
                      handleInputClick={this.handleInputClick}
                      model={model}
                    />
                  </>
              )}
              />
            )}
          </Fragment>
        );
      }
      case 'Ultrasound Vector Sensor':
      case 'Magnetometer Sensor':
      case 'Current Sensor': {
        return (
          <Fragment>
            <Labelcontainer>
              <ResourceItemLabel grey fontsize="14px" marginbottom="0" paddingtop="0" width="max-content">
                {t('sensors.detail.fullSpectrumMeasurementSettings')}
              </ResourceItemLabel>
            </Labelcontainer>
            <ResourceHeaderSection>{t('sensors.detail.sampling')}</ResourceHeaderSection>
            <Grid columns={3} doubling stackable>
             <Grid.Row>
              {highResEnabled && (
              <>
                <Grid.Column>
                  <Segment>
                   {this.renderField(
                     'select',
                     t('sensors.detail.highResolution'),
                     metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.highRes],
                     fieldValues[model].high_res.sampling.resolution,
                     false,
                     'high_res.sampling',
                     model
                   )}
                  </Segment>
                </Grid.Column>
                <Grid.Column>
                  <Segment>
                   {this.renderField(
                     'select',
                     t('sensors.detail.fullBandwidth'),
                     metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.fullBandwidth],
                     fieldValues[model].full_bandwidth.sampling.resolution,
                     false,
                     'full_bandwidth.sampling',
                     model
                   )}
                  </Segment>
                </Grid.Column>
              </>
              )}
              {!highResEnabled && (
               <Grid.Column>
                <Segment>
                 {this.renderField(
                   'select',
                   t('sensors.detail.resolution'),
                   metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.fullBandwidth],
                   fieldValues[model].full_bandwidth.sampling.resolution,
                   false,
                   'full_bandwidth.sampling',
                   model
                 )}
                </Segment>
               </Grid.Column>
              )}
             </Grid.Row>
            </Grid>

            <ResourceHeaderSection>{t('sensors.detail.measurement')}</ResourceHeaderSection>
            <Grid.Column>
              <Segment>
                <div style={{ display: 'flex' }}>
                  <div>
                   <ResourceItemLabel>{t('sensors.detail.type')}</ResourceItemLabel>
                   {
                    (
                      !_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                       fieldValues[model].measurement_type === 'schedule_based' &&
                       fieldValues[model].transmitter_version !== '0804'
                    ) &&
                       <Button onClick={() => this.setState({ openScheduler: true, modelOpened: model, typeOpened: type })} text fontsize="12px" style={{ paddingLeft: '120px' }}>{`${t('sensors.detail.set')} `}</Button>
                   }

                   {!_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                   fieldValues[model].transmitter_version !== '0804' && (
                    <>
                     {this.renderField(
                       'select',
                       '',
                       metadata[actualModel].spectrum_measurements.measurement.type,
                       fieldValues[model].measurement_type,
                       false,
                       'measurement_type',
                       model
                     )}
                    </>
                   )
                  }
                  </div>
                </div>
              </Segment>
            </Grid.Column>
           {this.props.currentAccount.preferences.tx_measurement_version === 'v1' ||
              fieldValues[model].transmitter_version === '0804' && (
               <Grid.Column>
                 {this.renderField(
                   'select',
                   t('sensors.detail.interval'),
                   metadata[actualModel].spectrum_measurements.measurement.intervals,
                   fieldValues[model].measurement_interval,
                   false,
                   'measurement_interval',
                   model
                 )}
               </Grid.Column>
              )
            }
            <ResourceHeaderSection>{t('sensors.detail.reporting')}</ResourceHeaderSection>
            <FlexContainer margintop="5px">
              <ExclamationTriangle size="sm" />
              <TextLabel>{t('sensors.detail.reportingIntervalDefinedInTransmitter')}</TextLabel>
            </FlexContainer>
          </Fragment>
        );
      }
      case 'VSX':
      case 'Vibration Sensor': {
        return (
          <Fragment>
            <Labelcontainer>
              <ResourceItemLabel grey fontsize="14px" marginbottom="0" paddingtop="0" width="max-content">
                {t('sensors.detail.fullSpectrumMeasurementSettings')}
              </ResourceItemLabel>
            </Labelcontainer>
            <ResourceHeaderSection>{t('sensors.detail.sampling')}</ResourceHeaderSection>
            <Grid columns={3} doubling stackable>
              <Grid.Row>
               {highResEnabled && (
                <>
                <Grid.Column>
                 <Segment>
                  {this.renderField(
                    'select',
                    t('sensors.detail.highResolution'),
                    metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.highRes],
                    fieldValues[model].high_res.sampling.resolution,
                    false,
                    'high_res.sampling',
                    model
                  )}
                 </Segment>
                </Grid.Column>
                <Grid.Column>
                 <Segment>
                  {this.renderField(
                    'select',
                    t('sensors.detail.fullBandwidth'),
                    metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.fullBandwidth],
                    fieldValues[model].full_bandwidth.sampling.resolution,
                    false,
                    'full_bandwidth.sampling',
                    model
                  )}
                 </Segment>
                </Grid.Column>
                </>
               )}
               {!highResEnabled && (
                <Grid.Column>
                 <Segment>
                  {this.renderField(
                    'select',
                    t('sensors.detail.resolution'),
                    metadata[actualModel].spectrum_measurements.sampling.resolution[vibrationMeasurementTypeEnum.fullBandwidth],
                    fieldValues[model].full_bandwidth.sampling.resolution,
                    false,
                    'full_bandwidth.sampling',
                    model
                  )}
                 </Segment>
                </Grid.Column>
               )}
              </Grid.Row>
            </Grid>

          <ResourceHeaderSection>{t('sensors.detail.highPassFilterCutoff')}</ResourceHeaderSection>
          <Grid columns={3} doubling stackable>
            <Grid.Row>
             {highResEnabled && (
              <>
              <Grid.Column>
               <Segment>
                 {this.renderField(
                   'select',
                   t('sensors.detail.highResolution'),
                   metadata[actualModel].spectrum_measurements.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.highRes],
                   fieldValues[model].high_res.high_pass_filter_cutoff,
                   false,
                   'high_res.high_pass_filter_cutoff',
                   model
                 )}
               </Segment>
              </Grid.Column>
              <Grid.Column>
               <Segment>
                {this.renderField(
                  'select',
                  t('sensors.detail.fullBandwidth'),
                  metadata[actualModel].spectrum_measurements.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.fullBandwidth],
                  fieldValues[model].full_bandwidth.high_pass_filter_cutoff,
                  false,
                  'full_bandwidth.high_pass_filter_cutoff',
                  model
                )}
               </Segment>
              </Grid.Column>
              </>
             )}
            {!highResEnabled && (
             <Grid.Column>
              <Segment>
                 {this.renderField(
                   'select',
                   '',
                   metadata[actualModel].spectrum_measurements.high_pass_filter_cutoff[vibrationMeasurementTypeEnum.fullBandwidth],
                   fieldValues[model].full_bandwidth.high_pass_filter_cutoff,
                   false,
                   'full_bandwidth.high_pass_filter_cutoff',
                   model
                 )}
              </Segment>
             </Grid.Column>
            )}
            </Grid.Row>
          </Grid>
            {/* <FlexContainer margintop="5px">
              <ExclamationTriangle size="sm" />
              <TextLabel>Minimum allowed measurement interval with selected sampling is 6 hours.</TextLabel>
            </FlexContainer> */}
          <ResourceHeaderSection>{t('sensors.detail.measurement')}</ResourceHeaderSection>
            <Grid.Column>
              <Segment>
                <div style={{ display: 'flex' }}>
                  <div>
                   <ResourceItemLabel>{t('sensors.detail.type')}</ResourceItemLabel>
                   {(!_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                     fieldValues[model].measurement_type === 'schedule_based' && fieldValues[model].transmitter_version !== '0804') &&
                    <SetButton onClick={() => this.setState({ openScheduler: true, modelOpened: model, typeOpened: type })} text fontsize="12px">{t('sensors.detail.set')}</SetButton>
                   }

                   {(!_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                   fieldValues[model].transmitter_version !== '0804') && (
                     this.renderField(
                       'select',
                       '',
                       metadata[actualModel].spectrum_measurements.measurement.type,
                       fieldValues[model].measurement_type,
                       false,
                       'measurement_type',
                       model))
                  }
                  </div>
                </div>
              </Segment>
            </Grid.Column>

            {(this.props.currentAccount.preferences.tx_measurement_version === 'v1' || fieldValues[model].transmitter_version === '0804') && (
             <Grid.Column>
              <Segment>
               {this.renderField(
                 'select',
                 t('sensors.detail.interval'),
                 metadata[actualModel].spectrum_measurements.measurement.intervals,
                 fieldValues[model].measurement_interval,
                 false,
                 'measurement_interval',
                 model
               )}
              </Segment>
             </Grid.Column>
            )}

            <ResourceHeaderSection>{t('sensors.detail.reporting')}</ResourceHeaderSection>
            <FlexContainer margintop="5px">
              <ExclamationTriangle size="sm" />
              <TextLabel>{t('sensors.detail.reportingIntervalDefinedInTransmitter')}</TextLabel>
            </FlexContainer>
          </Fragment>
        );
      }
      case 'Transmitter': {
        return (
          <Fragment>
           <ResourceHeaderSection>{t('sensors.detail.reporting')}</ResourceHeaderSection>
            <Grid columns={3} doubling stackable>
              <Grid.Row>
               <Grid.Column>
                <Segment>
                 {this.renderField(
                   'select',
                   t('sensors.detail.interval'),
                   metadata[actualModel].reporting.interval,
                   fieldValues[model].reporting_interval,
                   false,
                   'reporting_interval',
                   model
                 )}
                </Segment>
               </Grid.Column>
              </Grid.Row>
            </Grid>
            <RBAC
              yes={(
                <>
                  <ResourceHeaderSection>{t('sensors.detail.wifiSettings')}</ResourceHeaderSection>
                  <WifiSetting
                    ssid={fieldValues[model].ssid}
                    passphrase={fieldValues[model].passphrase}
                    errorMessages={fieldValues[model].errorMessages || {}}
                    wifi_schedule_date={fieldValues[model].wifi_schedule_date}
                    wifi_schedule_time={fieldValues[model].wifi_schedule_time}
                    preferredTimeOptions={this.preferredTimeOptions}
                    handleInputClick={this.handleInputClick}
                    model={model}
                  />
                  <ResourceHeaderSection>{t('sensors.detail.preferredAPSettingsShort')}</ResourceHeaderSection>
                  <PreferredAPSettings
                    bssid={fieldValues[model].bssid}
                    channel={fieldValues[model].channel}
                    errorMessages={fieldValues[model].errorMessages || {}}
                    handleInputClick={this.handleInputClick}
                    model={model}
                  />
                </>
              )}
            />
            {this.props.assignSensorVersionV2 && (
              <TxPorts
                txs={this.state.details.TX}
                editMode
                unassignSensor={this.unassignSensorFromTx}
                onAssignSensor={() => this.setState(prevState => ({ expandedContainers: { ...prevState.expandedContainers, TX: true } }))}
                onUpdateSensorSettings={this.props.sensorsActions.updateSensorDetails}
                onRefreshTxDetails={this.refreshTxDetails}
                user={this.props.user}
              />
            )}
          </Fragment>
        );
      }
      case 'other': {
        return (
          <Fragment>
            <Labelcontainer>
              <ResourceItemLabel grey fontsize="14px" marginbottom="0" paddingtop="0" width="350px">
                {t('sensors.detail.overallsMeasurementSettings')}
              </ResourceItemLabel>
            </Labelcontainer>
            <ResourceHeaderSection>{t('sensors.detail.measurement')}</ResourceHeaderSection>

            <Grid columns={3} doubling stackable>
             <Grid.Row>
              <Grid.Column>
                <Segment>
                 <div style={{ display: 'flex' }}>
                  <div>
                   <ResourceItemLabel>{t('sensors.detail.type')}</ResourceItemLabel>
                   {(!_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                    fieldValues[model].overall_measurement_type === 'schedule_based' &&
                    fieldValues[model].transmitter_version !== '0804') && (
                     <Button text onClick={() => this.setState({ openScheduler: true, modelOpened: model, typeOpened: type })} fontsize="12px">
                       {t('sensors.detail.setSchedule')}
                     </Button>
                   )}
                   {(!_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
                    fieldValues[model].transmitter_version !== '0804') && this.renderField(
                     'select',
                     '',
                     metadata.other.measurement_settings.type,
                     fieldValues[model].overall_measurement_type,
                     false,
                     'overall_measurement_type',
                     model)}
                  </div>
                 </div>
                </Segment>
              </Grid.Column>
              {(this.props.currentAccount.preferences.tx_measurement_version === 'v1' || fieldValues[model].transmitter_version === '0804') && (
               <Grid.Column>
                <Segment>
                 {this.renderField(
                   'select',
                   t('sensors.detail.interval'),
                   metadata.other.measurement_settings.intervals,
                   fieldValues[model].overall_measurement_interval,
                   false,
                   'overall_measurement_interval',
                   model
                 )}
                </Segment>
               </Grid.Column>
              )}
             </Grid.Row>
            </Grid>
            <ResourceHeaderSection>{t('sensors.detail.reporting')}</ResourceHeaderSection>
            <FlexContainer>
              <ExclamationTriangle size="sm" />
              <TextLabel>{t('sensors.detail.reportingIntervalDefinedInTransmitter')}</TextLabel>
            </FlexContainer>
          </Fragment>
        );
      }
      default:
        return t('sensors.errors.invalidSensorType');
    }
  };

  saveSchedule = (schedule, stops) => {
    const { fieldValues, modelOpened, typeOpened } = this.state;
    if (typeOpened === 'Vibration Mote') {
      // Describe only the blocks the model reports — the rows for the others are
      // disabled in the modal, and writing a schedule onto a placeholder block
      // would just be dropped again by makeResponseForType.
      const availableBlocks = fieldValues[modelOpened].available_blocks || allMeasurementBlockFields;
      const blockSpec = {};
      vibrationMeasurementBlocks.forEach(({ index, field }) => {
        if (availableBlocks.includes(field)) {
          blockSpec[field] = { schedule: { $set: schedule[index] }, stops: { $set: stops[index] } };
        }
      });
      const newDetails = update(fieldValues, {
        [modelOpened]: {
          ...blockSpec,
          scheduled_saved: { $set: true }
        }
      });
      this.setState({ fieldValues: newDetails, openScheduler: false }, this.onSave);
    } else if (VectorSensors.includes(typeOpened)) {
      const newDetails = update(fieldValues, {
        [modelOpened]: {
          full_bandwidth: { measurement: {
            schedule: { $set: schedule[1] },
            stops: { $set: stops[1] }
          } },
          high_res: { measurement: {
            schedule: { $set: schedule[2] },
            stops: { $set: stops[2] }
          } },
          scheduled_saved: { $set: true }
        }
      });
      this.setState({ fieldValues: newDetails, openScheduler: false }, this.onSave);
    } else if (typeOpened === 'other') {
      const newDetails = update(fieldValues, {
        [modelOpened]: {
          overall_measurement_schedule: { $set: schedule[0] },
          overall_measurement_stops: { $set: stops[0] },
          scheduled_saved: { $set: true }
        }
      });
      this.setState({ fieldValues: newDetails, openScheduler: false }, this.onSave);
    }
  };

  saveDailySchedule = (schedule, stops) => {
    const { fieldValues } = this.state;
    const newDetails = update(fieldValues, {
      other: {
        overall_measurement_schedule: { $set: schedule },
        overall_measurement_stops: { $set: stops }
      }
    });
    this.setState({ fieldValues: newDetails });
  };

  renderAggressiveModal = (modelOpened) => {
    const { metadata, t } = this.props;
    const { details } = this.state;
    return (
      <div> {
        this.renderField(
          'select',
          t('sensors.aggressiveMeasurement.measurementTimeOutInterval'),
          metadata.aggressive_measurement_timeout.interval,
          details.aggressive_measurement_timeout,
          false,
          'aggressive_measurement_timeout',
          modelOpened
        )
      }
      {
        metadata[modelOpened].reporting &&
        metadata[modelOpened].reporting.interval &&
        this.renderField(
          'select',
          t('sensors.detail.reportingInterval'),
          _.filter(
            metadata[modelOpened].reporting.interval,
            o => o.value <= AGGRESSIVE_INTERVAL_THRESHOLD
          ),
          details.reporting_interval,
          false,
          'reporting_interval',
          modelOpened
        )
      }
      </div>
    );
  }

  renderReportingIntervalModal = (modelOpened, ri_upper_threshold) => {
    const { metadata, t } = this.props;
    const { details } = this.state;

    return (
      <div>
        {
          metadata[modelOpened].reporting &&
          metadata[modelOpened].reporting.interval &&
          this.renderField(
            'select',
            t('sensors.detail.reportingInterval'),
            _.filter(
              metadata[modelOpened].reporting.interval,
              o => o.value > AGGRESSIVE_INTERVAL_THRESHOLD
            ),
            details[modelOpened].reporting_interval,
            false,
            'reporting_interval',
            modelOpened,
            ri_upper_threshold
          )
      }
      </div>
    );
  }

  refreshTxDetails = () => {
    const { details } = this.state;
    if (details && details.TX) {
      details.TX.forEach(tx => (
        this.props.sensorsActions.getSensorDetails(tx.serial_number)
      ));
    }
  };

  render() {
    const {
      sensorList,
      saveEnabled,
      error,
      details,
      fieldValues,
      modelOpened,
      typeOpened,
      actualModel
    } = this.state;
    const {
      metadata,
      loading,
      detailsError,
      errorList,
      updating,
      utcOffset,
      t,
      i18n,
      user
    } = this.props;
    let overall = [];
    let overall_stops = [];
    let full_bandwidth = [];
    let full_bandwidth_stops = [];
    let high_res = [];
    let high_res_stops = [];
    let intervals = [];
    let disabledRows = {};
    let component = null;
    let reportingIntervalComponent = null;
    let disableReportingProceed = null;

    let ri_upper_threshold = 1e9;
    if (details && details.VM3) {
      const thresolds = [];
      details.VM3.forEach(
        entry => thresolds.push(sensorUtils.getVersionThreshold(entry.firmware_versions.mcu))
      );
      if (!_.isEmpty(thresolds)) ri_upper_threshold = _.min(thresolds);
    }

    if (details && details.VM4P) {
      const thresolds = [];
      details.VM4P.forEach(
        entry => thresolds.push(sensorUtils.getVersionThreshold(entry.firmware_versions.mcu))
      );
      if (!_.isEmpty(thresolds)) ri_upper_threshold = _.min(thresolds);
    }

    if (details && details.GSP) {
      const thresolds = [];
      details.GSP.forEach(
        entry => thresolds.push(sensorUtils.getVersionThreshold(entry.firmware_versions.mcu))
      );
      if (!_.isEmpty(thresolds)) ri_upper_threshold = _.min(thresolds);
    }

    if (details && details.VM4) {
      const thresolds = [];
      details.VM4.forEach(
        entry => thresolds.push(sensorUtils.getVersionThreshold(entry.firmware_versions.mcu))
      );
      if (!_.isEmpty(thresolds)) ri_upper_threshold = _.min(thresolds);
    }

    if (details && details.GS) {
      const thresolds = [];
      details.GS.forEach(
        entry => thresolds.push(sensorUtils.getVersionThreshold(entry.firmware_versions.mcu))
      );
      if (!_.isEmpty(thresolds)) ri_upper_threshold = _.min(thresolds);
    }

    if (typeOpened === 'Vibration Mote') {
      const availableBlocks = fieldValues[modelOpened].available_blocks || allMeasurementBlockFields;
      // A block the model does not report is an empty placeholder here, so its
      // schedule/stops are undefined — the modal maps over both and would throw.
      // Empty arrays match its own internal defaults.
      overall = fieldValues[modelOpened].overall.schedule || [];
      full_bandwidth = fieldValues[modelOpened].full_bandwidth.schedule || [];
      high_res = fieldValues[modelOpened].high_res.schedule || [];
      overall_stops = fieldValues[modelOpened].overall.stops || [];
      full_bandwidth_stops = fieldValues[modelOpened].full_bandwidth.stops || [];
      high_res_stops = fieldValues[modelOpened].high_res.stops || [];
      intervals = metadata[actualModel].measurement_settings.intervals;
      // Rows for blocks the model does not report stay disabled alongside the
      // temperature-only case: saveSchedule and makeResponseForType both leave
      // them out, so an editable row would silently discard the user's work.
      disabledRows = {
        0: !availableBlocks.includes('overall'),
        // Disable full bandwidth for temperature-only motes
        1: modelOpened.includes('Temperature Only') || !availableBlocks.includes('full_bandwidth'),
        // Disable high res for temperature-only motes
        2: modelOpened.includes('Temperature Only') || !availableBlocks.includes('high_res')
      };
      component = this.renderAggressiveModal(actualModel);
      reportingIntervalComponent = (
        ['VM1', 'VM2'].includes(modelOpened) ?
          null : this.renderReportingIntervalModal(actualModel, ri_upper_threshold)
      );
      disableReportingProceed = (
        ['VM3', 'VM4', 'VM4P', 'GS', 'GSP', 'VM4P-IS'].includes(modelOpened) &&
        fieldValues[modelOpened].reporting_interval < AGGRESSIVE_INTERVAL_THRESHOLD
      );
    } else if (VectorSensors.includes(typeOpened)) {
      const highResEnabled = !_.includes(oldTxMeasurementVersion, this.props.currentAccount.preferences.tx_measurement_version) &&
        (typeOpened === 'Vibration Sensor' || typeOpened === 'Current Sensor' || typeOpened === 'VSX');
      overall = [];
      overall_stops = [];
      full_bandwidth = [];
      full_bandwidth_stops = [];
      high_res = [];
      high_res_stops = [];
      if (fieldValues[modelOpened].full_bandwidth.measurement.schedule) {
        full_bandwidth = fieldValues[modelOpened].full_bandwidth.measurement.schedule;
        full_bandwidth_stops = fieldValues[modelOpened].full_bandwidth.measurement.stops;
      }
      if (highResEnabled && fieldValues[modelOpened].high_res.measurement.schedule) {
        high_res = fieldValues[modelOpened].high_res.measurement.schedule;
        high_res_stops = fieldValues[modelOpened].high_res.measurement.stops;
      }
      intervals = metadata[modelOpened].spectrum_measurements.measurement.intervals;
      disabledRows = {
        0: true,
        1: false,
        2: !highResEnabled
      };
      component = this.renderAggressiveModal(modelOpened);
    } else if (typeOpened === 'other') {
      overall = fieldValues.other.overall_measurement_schedule;
      overall_stops = fieldValues.other.overall_measurement_stops;
      high_res = [];
      high_res_stops = [];
      full_bandwidth = [];
      full_bandwidth_stops = [];
      intervals = metadata.other.measurement_settings.intervals;
      disabledRows = {
        0: false,
        1: true,
        2: true
      };
    }

    const { dissociatePrompt } = this.state;
    // A Tx whose sensors are on no machine gets wiring-specific wording; the
    // machine copy would name something the user never sees.
    const promptRows = dissociatePrompt
      ? (this.state.details[dissociatePrompt.modelKey] || [])
        .filter(d => dissociatePrompt.serials.includes(d.serial_number))
      : [];
    const wiringOnly = promptRows.length > 0
      && promptRows.every(this.isTransmitterWithAttachedSensors);
    let dissociateMessage = '';
    if (dissociatePrompt) {
      if (wiringOnly) {
        dissociateMessage = dissociatePrompt.serials.length === 1
          ? t('sensors.grouped.unassignConfirmSingle', {
            serial: dissociatePrompt.serials[0],
            defaultValue: 'Unassign all sensors from the ports of {{serial}}?'
          })
          : t('sensors.grouped.unassignConfirmMultiple', {
            count: dissociatePrompt.serials.length,
            defaultValue: 'Unassign all sensors from the ports of {{count}} transmitters?'
          });
      } else {
        dissociateMessage = dissociatePrompt.serials.length === 1
          ? t('sensors.grouped.dissociateConfirmSingle', { serial: dissociatePrompt.serials[0] })
          : t('sensors.grouped.dissociateConfirmMultiple', { count: dissociatePrompt.serials.length });
      }
    }
    const dissociateHint = wiringOnly
      ? t('sensors.grouped.unassignConfirmHint', {
        defaultValue: 'Its sensors are unassigned from the transmitter and stay in this site, keeping any machine assignment of their own. You can reassign the transmitter to a different site once its ports are empty.'
      })
      : t('sensors.grouped.dissociateConfirmHint');

    return (
      <Fragment>
        {dissociatePrompt && (
          <DissociateConfirmPrompt
            message={dissociateMessage}
            secondaryMessage={dissociateHint}
            onProceed={this.confirmDissociateFromPrompt}
            onCancel={this.closeDissociatePrompt}
            proceedText={wiringOnly
              ? t('sensors.grouped.unassignSensors', { defaultValue: 'Unassign sensors' })
              : t('sensors.detail.dissociate')}
            cancelText={t('sensors.detail.cancel')}
            proceedColor={colors.greenXD}
            width="440px"
          />
        )}
        {this.state.openScheduler && (
          <MeasurementScheduleModal
            close={() => this.setState({ openScheduler: false })}
            editMode={this.state.editMode}
            schedule={{
              0: overall,
              1: full_bandwidth,
              2: high_res
            }}
            stops={{
              0: overall_stops,
              1: full_bandwidth_stops,
              2: high_res_stops
            }}
            disabledRows={disabledRows}
            intervals={intervals}
            saveSchedule={this.saveSchedule}
            utcOffset={utcOffset}
            component={() => component}
            reportingIntervalComponent={reportingIntervalComponent}
            onProceedDisabledReporting={disableReportingProceed}
            restrictHighRes={typeOpened === 'Vibration Mote'}
            temperatureOnly={modelOpened.includes('Temperature Only')}
          />
        )}
        <Container>
          {updating && (
            <AbsoluteLoading>
              <LoadingSvg />
            </AbsoluteLoading>
          )}
          {loading && !error && !detailsError && (
            <LoadingContainer>
              <LoadingSvg />
            </LoadingContainer>
          )}
          {!loading && (error || detailsError) && (
            <div>{error || detailsError} <b>{t('sensors.detail.errorSerialNumbersSuffix', { serialNumbers: errorList.join(', ') })}</b></div>
          )
          }
          {!loading && !error && (
            <Fragment>
              <HeaderContainer>
                <H2>{t('sensors.detail.editMultipleSensors', { count: sensorList.length })}</H2>
                <SvgContainer onClick={updating ? undefined : this.props.close} style={{ opacity: updating ? 0.5 : 1, cursor: updating ? 'not-allowed' : 'pointer' }}>
                  <CrossSvg width={25} height={25} />
                </SvgContainer>
              </HeaderContainer>
              {Object.entries(details).map((entry, idx) => (
                entry[1][0] && (
                <ExpandableContainer
                  key={idx}
                  title={['other', 'other - 0804'].includes(entry[0])
                    ? calculateOtherSensorGroupedName(entry[1], t, i18n)
                    : `${getSensorTypeDisplayName(t, i18n, entry[1][0].type)} - ${entry[0]}`}
                  count={entry[1].length}
                  defaultState={this.state.expandedContainers[entry[0]]}
                  headerActions={this.renderBulkDissociateDropdown(entry[0])}
                >
                  {this.renderDebugModeHint(entry[1])}
                  {this.renderContent(entry[1][0].type, entry[0], entry[1][0].model, ri_upper_threshold)}
                </ExpandableContainer>
                )))}
              <ButtonsContainer>
                <Button disabled={!saveEnabled || updating} onClick={this.onSave}>{t('sensors.detail.save')}</Button>
                <Button secondary="#3C3D3A" disabled={!saveEnabled || updating} onClick={this.onCancel}>{t('sensors.detail.cancel')}</Button>
                {(() => {
                  const ctx = this.getReassignContext();
                  if (!ctx.orgId || !(isPetasenseAdmin(user) || isOrgAdmin(user))) return null;
                  const blocked = !ctx.canReassign;
                  const busy = this.state.reassigning || updating;
                  // Every reason the button is dead needs an explanation, not
                  // just the eligibility gate.
                  let hint = '';
                  if (blocked) hint = this.reassignDisabledHint(ctx);
                  else if (busy) {
                    hint = t('sensors.reassignSite.disabledHintBusy', {
                      defaultValue: 'Waiting for the current update to finish.'
                    });
                  }
                  return (
                    <Fragment>
                      <Button
                        secondary="#000"
                        style={{ marginLeft: '15px' }}
                        disabled={blocked || busy}
                        title={hint}
                        onClick={() => this.setState({ showReassignSiteModal: true })}
                      >
                        {t('sensors.reassignSite.cta')}
                      </Button>
                      {(blocked || busy) && (
                        <ReassignNote
                          message={t('sensors.reassignSite.disabledNote', {
                            defaultValue: 'Why is this disabled?'
                          })}
                          detail={hint}
                          nowrap
                        />
                      )}
                    </Fragment>
                  );
                })()}
              </ButtonsContainer>
              {this.state.showReassignSiteModal && (() => {
                const ctx = this.getReassignContext();
                return (
                  <ReassignToSiteModal
                    orgId={ctx.orgId}
                    deviceCount={ctx.selected.length}
                    deviceAccountIds={ctx.selected
                      .map(s => s.account_id)
                      .filter(id => id != null)}
                    close={() => this.setState({ showReassignSiteModal: false })}
                    onConfirm={this.handleReassignSelectedToSite}
                  />
                );
              })()}
            </Fragment>
          )}
        </Container>
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  metadata: state.sensors.metadata.metadata,
  details: state.sensors.details.details,
  currentAccount: state.currentAccount,
  loading: state.sensors.details.loading,
  detailsError: state.sensors.details.error,
  updating: state.sensors.details.updating,
  errorList: state.sensors.details.errorList,
  user: state.user.user,
  sensorItems: state.sensors.items.object,
  selectedSensorList: state.sensors.selectedSensorList
});

const mapDispatchToProps = dispatch => ({
  sensorsActions: bindActionCreators(sensorsActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(withTranslation(undefined, { withRef: true })(GroupedSensors));
